import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import RegistrationStepperHeader from '../components/RegistrationStepperHeader';
import StepperButtons from '../components/StepperButtons';
import Swal from 'sweetalert2';

const BookingOverview = () => {
  const navigate = useNavigate();
  const { selectedEvent, selectedCenter } = useSelector((state) => state.event);
  const {
    eventDetails,
    selectedService,
    selectedOptionalServices,
    selectedTimeOption,
    devoteeInfo,
    familyMembers,
    selectedFamilyMemberIds,
    totalPrice,
  } = useSelector((state) => state.booking);

  const [isAgreementChecked, setIsAgreementChecked] = useState(false);
  const [isAgreementModalOpen, setIsAgreementModalOpen] = useState(false);

  // Removed incomplete booking check - users reach this page through proper navigation flow
  // The routing and stepper components handle the flow validation

  const selectedMembers = familyMembers.filter(m => selectedFamilyMemberIds.includes(m.id));

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const calculateAge = (dateOfBirth) => {
    if (!dateOfBirth) return '—';
    try {
      const dob = new Date(dateOfBirth);
      const today = new Date();
      let age = today.getFullYear() - dob.getFullYear();
      const monthDiff = today.getMonth() - dob.getMonth();
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
        age--;
      }
      return age;
    } catch (error) {
      return '—';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 pb-24">
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-6xl mx-auto">
          <div className="overflow-hidden rounded-[28px] bg-white/70 backdrop-blur-xl shadow-[0_26px_70px_-45px_rgba(15,23,42,0.55)] border border-white/60 mb-4">
            <RegistrationStepperHeader
              title={selectedEvent?.event_Name || 'Event Registration'}
              subtitle={selectedEvent?.event_Date ? `${formatDate(selectedEvent.event_Date)}${selectedCenter?.center_Name ? ` • ${selectedCenter.center_Name}` : ''}` : null}
              backPath="/family"
              backLabel="Back"
              eventDetails={eventDetails}
            />

            <div className="p-6">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 space-y-4">
                  <div className="rounded-2xl bg-white/85 border border-gray-200/70 shadow-sm">
                    <div className="px-5 pt-4 pb-3 border-b border-gray-200/70">
                      <div className="flex items-start justify-between gap-3 mb-3">
                        <div>
                          <div className="text-sm font-semibold text-gray-900">Review Summary</div>
                          <div className="text-xs text-gray-600 mt-1">Please confirm details and agree to terms and conditions</div>
                        </div>
                      </div>
                      <div className="rounded-xl bg-violet-50/50 border border-violet-200 p-4">
                        <label className="flex items-start gap-3 text-xs text-gray-700 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={isAgreementChecked}
                            onChange={(e) => setIsAgreementChecked(e.target.checked)}
                            className="mt-0.5 h-5 w-5 rounded border-gray-300 text-violet-600 focus:ring-violet-500 cursor-pointer"
                          />
                          <div className="flex-1">
                            <span className="leading-relaxed font-medium text-gray-800">
                              I have reviewed all details above and agree to the event terms &amp; conditions
                            </span>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.preventDefault();
                                setIsAgreementModalOpen(true);
                              }}
                              className="block mt-1.5 text-[11px] font-semibold text-violet-700 hover:text-violet-800 hover:underline"
                            >
                              📄 View Full Agreement
                            </button>
                            {!isAgreementChecked && (
                              <p className="mt-2 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-2 py-1.5">
                                ⚠️ Please check this box to proceed to payment
                              </p>
                            )}
                          </div>
                        </label>
                      </div>
                    </div>

                    <div className="p-5 space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div className="rounded-2xl bg-white border border-gray-200 px-4 py-3">
                          <div className="text-[11px] font-semibold text-gray-500">Devotee</div>
                          <div className="mt-1 text-xs text-gray-800">
                            {devoteeInfo.firstName || devoteeInfo.lastName ? `${devoteeInfo.firstName} ${devoteeInfo.lastName}` : '—'}
                          </div>
                          <div className="mt-2 text-[11px] text-gray-500">Phone Number</div>
                          <div className="text-xs text-gray-800">{devoteeInfo.phone || '—'}</div>
                          <div className="mt-2 text-[11px] text-gray-500">Email</div>
                          <div className="text-xs text-gray-800 truncate">{devoteeInfo.email || '—'}</div>
                        </div>

                        <div className="rounded-2xl bg-white border border-gray-200 px-4 py-3">
                          <div className="text-[11px] font-semibold text-gray-500 mb-2">
                            Family Members ({selectedMembers.length})
                          </div>
                          {selectedMembers.length > 0 ? (
                            <div className="space-y-1.5">
                              {selectedMembers.map((member, index) => (
                                <div key={index} className="flex items-center gap-2 bg-blue-50 px-2 py-1.5 rounded-lg">
                                  <span className="text-sm">{member.gender?.toLowerCase() === 'male' || member.gender?.toLowerCase() === 'm' ? '👦' : '👧'}</span>
                                  <div className="flex-1 min-w-0">
                                    <p className="text-gray-800 font-medium text-[11px] truncate">{member.firstName} {member.lastName}</p>
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="text-xs text-gray-500">No family members selected</div>
                          )}
                        </div>
                      </div>

                      <div className="rounded-2xl bg-white border border-gray-200 overflow-hidden">
                        <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-gray-100">
                          <div className="text-xs font-semibold text-gray-600">Selected Seva</div>
                          <div className="text-xs font-semibold text-gray-900 text-right">
                            {selectedService?.service_Name || '—'}
                            {selectedService?.service_Price != null ? ` ($${Number(selectedService.service_Price).toFixed(2)})` : ''}
                          </div>
                        </div>
                        {selectedTimeOption && (
                          <div className="px-4 py-3 border-b border-gray-100">
                            <div className="text-xs font-semibold text-gray-600 mb-1">Selected Timing</div>
                            <div className="text-xs font-semibold text-gray-900">
                              {selectedTimeOption?.time_Option_Name || '—'}
                            </div>
                            {selectedTimeOption?.time_Option_Description && (
                              <div className="mt-1 text-[11px] text-gray-600">
                                {selectedTimeOption.time_Option_Description}
                              </div>
                            )}
                          </div>
                        )}
                        {selectedOptionalServices && selectedOptionalServices.length > 0 && (
                          <div className="px-4 py-3 border-b border-gray-100">
                            <div className="text-xs font-semibold text-gray-600 mb-2">Add-on Sevas</div>
                            <div className="space-y-1.5">
                              {selectedOptionalServices.map((addon, index) => (
                                <div key={index} className="flex items-center justify-between gap-2 text-xs">
                                  <span className="text-gray-700">{addon.service_Name || addon.optional_Service_Name}</span>
                                  <span className="text-gray-900 font-medium">${Number(addon.service_Price || addon.optional_Service_Price || 0).toFixed(2)}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        <div className="flex items-center justify-between gap-3 px-4 py-3">
                          <div className="text-xs font-semibold text-gray-600">Total Amount</div>
                          <div className="text-xs font-semibold text-gray-900">${Number(totalPrice || 0).toFixed(2)}</div>
                        </div>
                      </div>
                    </div>
                  </div>

                </div>

                <div className="rounded-2xl bg-white/85 border border-gray-200/70 shadow-sm">
                  <div className="px-5 pt-4 pb-3 border-b border-gray-200/70">
                    <div className="text-sm font-semibold text-gray-900">Invoice</div>
                  </div>

                  <div className="p-5 space-y-3">
                    <div className="rounded-2xl bg-white border border-gray-200 px-4 py-3">
                      <div className="flex items-center justify-between gap-3 mb-2">
                        <div className="text-xs font-semibold text-gray-700">Seva (Per Person)</div>
                        <div className="text-xs font-semibold text-gray-900">${Number(selectedService?.service_Price || 0).toFixed(2)}</div>
                      </div>
                      <div className="text-[11px] text-gray-600">
                        {1 + selectedMembers.length} {1 + selectedMembers.length === 1 ? 'Attendee' : 'Attendees'} (1 Devotee{selectedMembers.length > 0 ? ` + ${selectedMembers.length} Family ${selectedMembers.length === 1 ? 'Member' : 'Members'}` : ''})
                      </div>
                    </div>
                    <div className="flex items-center justify-between gap-3 rounded-2xl bg-gradient-to-br from-indigo-50 to-purple-50 border border-indigo-200 px-4 py-3">
                      <div className="text-xs font-semibold text-gray-900">Total</div>
                      <div className="text-sm font-bold text-gray-900">${Number(totalPrice || 0).toFixed(2)}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {isAgreementModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-3"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) {
              setIsAgreementModalOpen(false);
            }
          }}
          role="dialog"
          aria-modal="true"
        >
          <div className="w-full max-w-xl rounded-2xl bg-white shadow-2xl border border-white/60 overflow-hidden">
            <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-gray-200 bg-gradient-to-br from-white via-slate-50 to-violet-50">
              <div className="min-w-0">
                <div className="text-sm font-semibold text-gray-900">Maha Shivaratri Seva Agreement</div>
                <div className="text-xs text-gray-500">Please read before proceeding.</div>
              </div>
              <button
                type="button"
                onClick={() => setIsAgreementModalOpen(false)}
                className="shrink-0 inline-flex h-9 w-9 items-center justify-center rounded-full bg-white hover:bg-gray-50 text-gray-700 border border-gray-200 shadow-sm"
                aria-label="Close"
              >
                <i className="fas fa-times text-xs"></i>
              </button>
            </div>

            <div className="p-5">
              <div className="rounded-2xl bg-white border border-gray-200 px-4 py-4">
                <ul className="list-disc pl-5 text-xs text-gray-700 space-y-2">
                  <li>Seva / donation amounts are voluntary and may be non-refundable.</li>
                  <li>Seva allotment and timing may change based on temple schedule.</li>
                  <li>Please arrive on time and follow temple guidelines.</li>
                  <li>For questions, contact coordinators listed on the flyer.</li>
                </ul>
              </div>

              <div className="mt-4 flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setIsAgreementModalOpen(false)}
                  className="rounded-full bg-white hover:bg-gray-50 px-4 py-2 text-xs font-semibold text-gray-700 border border-gray-200 shadow-sm"
                >
                  Close
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setIsAgreementChecked(true);
                    setIsAgreementModalOpen(false);
                  }}
                  className="rounded-full bg-gradient-to-r from-indigo-600 via-purple-600 to-amber-500 px-4 py-2 text-xs font-semibold text-white shadow-md"
                >
                  I Agree
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <StepperButtons
        wrapperClassName="bg-white/90 backdrop-blur-xl"
        containerClassName="px-4 py-3"
        backPath="/family"
        nextPath="/event-payment"
        nextLabel="Proceed to Payment"
        nextDisabled={!isAgreementChecked}
        centerContent={
          <div className="text-xs text-gray-500">
            Total:
            <span className="ml-2 text-sm font-semibold text-gray-900">
              ${Number(totalPrice || 0).toFixed(2)}
            </span>
          </div>
        }
      />
    </div>
  );
};

export default BookingOverview;
